import { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import PublicHeader from "@/components/PublicHeader";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Check, Crown, Sparkle, ArrowRight, Lightning, CircleNotch, SealCheck, Coins, Receipt } from "@phosphor-icons/react";

const FEATURES = {
  trial: [
    "Every feature unlocked for 3 days",
    "Unlimited Fantastic Finds scans + AI listing copy",
    "Recover existing inventory & list new inventory",
    "5 cross-lists to the platforms you choose",
    "Your inventory is always yours to export — even if you choose not to stick with us!",
  ],
  member: [
    "Rescue your hard work from singular platforms",
    "Pull out your full inventory — kept for good",
    "20 cross-lists every month on currently offered platforms (more to come!)",
    "Decision-support pricing + best-fit placement",
    "Voice-matched, list-ready copy + send-to-Drafts",
    "Add credit packs anytime to list more",
  ],
  annual: [
    "Everything in Monthly — with NO LIMITS!",
    "Unlimited cross-listing across every currently offered platform (more to come!)",
    "Unlimited AI drafts, stats & inventory intelligence",
    "Unlimited Fantastic Finds identifier",
    "Each new listing is easier than the last!",
    "Save ~3 months vs. paying monthly and adding credits",
  ],
  founding: [
    "Everything Annual has — unlimited, forever",
    "Your $29 rate locked in for life",
    "Founding Council badge on your finds",
    "A direct hand in shaping the roadmap",
  ],
};

const money = (n) => `$${Number(n || 0) % 1 === 0 ? Number(n) : Number(n).toFixed(2)}`;

export default function PlansPage() {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    api.get("/billing/plans").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  const sessionId = params.get("session_id");

  const pollStatus = useCallback(async (sid, tries = 0) => {
    if (tries > 7) {
      setVerifying(false);
      toast.error("Still confirming your payment — refresh in a moment if it doesn't update.");
      return;
    }
    try {
      const { data: st } = await api.get(`/billing/status/${sid}`);
      if (st.payment_status === "paid") {
        setVerifying(false);
        await checkAuth();
        toast.success("You're in! Welcome aboard. 🎉");
        setParams({}, { replace: true });
        return;
      }
      if (st.status === "expired") {
        setVerifying(false);
        toast.error("That checkout session expired. No charge — feel free to try again.");
        setParams({}, { replace: true });
        return;
      }
      setTimeout(() => pollStatus(sid, tries + 1), 2000);
    } catch (e) {
      setTimeout(() => pollStatus(sid, tries + 1), 2000);
    }
  }, [checkAuth, setParams]);

  useEffect(() => {
    if (sessionId) {
      setVerifying(true);
      pollStatus(sessionId);
    }
  }, [sessionId]); // eslint-disable-line

  const subscribe = async (planId) => {
    if (!user) {
      toast.message("Start your free trial first — takes 10 seconds.");
      navigate("/register");
      return;
    }
    setBusy(planId);
    try {
      const { data: res } = await api.post("/billing/checkout", {
        plan_id: planId,
        origin_url: window.location.origin,
      });
      window.location.href = res.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't start checkout. Try again.");
      setBusy("");
    }
  };

  const monthly = data?.plans?.find((p) => p.id === "member_monthly");
  const annual = data?.plans?.find((p) => p.id === "member_annual");
  const founding = data?.plans?.find((p) => p.id === "founding");
  const ps = user?.plan_status;
  const isMember = ps?.active;
  const spotsLeft = data ? Math.max(0, data.founding_spots_total - data.founding_spots_taken) : null;

  return (
    <div className="min-h-screen bg-[#FDFBF7] text-[#0D1B2A]" data-testid="plans-page">
      <PublicHeader />

      {verifying && (
        <div className="fixed inset-0 z-50 bg-[#0D1B2A]/70 backdrop-blur-sm flex items-center justify-center" data-testid="billing-verifying">
          <div className="bg-[#FDFBF7] px-8 py-7 max-w-sm text-center border border-[#E5E0D6]">
            <CircleNotch size={40} className="mx-auto animate-spin text-[#B47A3C]" weight="bold" />
            <p className="font-serif-display text-2xl font-bold mt-4">Confirming your payment…</p>
            <p className="text-sm text-[#57534E] mt-1">One moment — unlocking your membership.</p>
          </div>
        </div>
      )}

      <div className="max-w-6xl mx-auto px-6 py-14">
        <div className="max-w-2xl">
          <p className="label-caps text-[#B47A3C] mb-3">Membership</p>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
            Price it against a single sale — not a monthly bill.
          </h1>
          <p className="text-base sm:text-lg text-[#57534E] mt-5 leading-relaxed">
            Start with a <strong>3-day free trial</strong> — every feature unlocked. When you're ready to price,
            place, and cross-list like a pro, one good flip covers your membership for months.
          </p>
        </div>

        {isMember && (
          <div className="mt-8 inline-flex items-center gap-2 border border-[#0D1B2A] bg-[#0D1B2A] text-[#FDFBF7] px-4 py-2" data-testid="current-plan-banner">
            <SealCheck size={18} weight="fill" className="text-[#B47A3C]" />
            <span className="text-sm font-semibold">
              You're on {ps.label}{ps.expires_at ? ` · renews ${new Date(ps.expires_at).toLocaleDateString()}` : ""}
            </span>
          </div>
        )}

        {/* Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mt-10 items-stretch">
          {/* Free Trial */}
          <PlanCard
            testid="plan-card-free"
            name="Free Trial"
            price="Free"
            period="for 3 days"
            subnote="All features + 5 cross-lists"
            blurb={data?.free?.blurb}
            features={FEATURES.trial}
            footNote="🔒 Card required — one trial per person, so Founding pricing stays fair."
            cta={
              <button
                data-testid="plan-cta-free"
                onClick={() => navigate(user ? "/finds" : "/register")}
                className="w-full h-12 border border-[#0D1B2A] font-semibold text-sm hover:bg-[#0D1B2A] hover:text-[#FDFBF7] transition-colors inline-flex items-center justify-center gap-2"
              >
                <Sparkle size={16} weight="fill" /> {user ? "Start scouting" : "Start free trial"}
              </button>
            }
          />

          {/* Monthly */}
          <PlanCard
            testid="plan-card-member"
            name="Monthly"
            price={monthly ? money(monthly.amount) : "—"}
            period="/month"
            subnote="20 cross-lists / month included"
            blurb={monthly?.blurb}
            features={FEATURES.member}
            featuresLead="Everything in the trial, plus:"
            footNote="Need more? 50 credits $20 (+10 cross-lists) · 100 credits $35 (+20). 5 credits per cross-list."
            cta={
              <button
                data-testid="plan-cta-member"
                disabled={!!busy}
                onClick={() => subscribe("member_monthly")}
                className="w-full h-12 bg-[#0D1B2A] text-[#FDFBF7] font-semibold text-sm hover:bg-black transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60"
              >
                <Lightning size={16} weight="fill" /> {busy === "member_monthly" ? "Opening checkout…" : "Go Monthly"}
              </button>
            }
          />

          {/* Annual Unlimited (featured) */}
          <PlanCard
            testid="plan-card-annual"
            featured
            badge="Best value"
            name="Annual Unlimited"
            price={annual ? money(annual.amount) : "—"}
            period="/year"
            subnote="Unlimited everything · save ~3 months + credit top-ups"
            blurb={annual?.blurb}
            features={FEATURES.annual}
            cta={
              <button
                data-testid="plan-cta-annual"
                disabled={!!busy}
                onClick={() => subscribe("member_annual")}
                className="w-full h-12 bg-[#0D1B2A] text-[#FDFBF7] font-semibold text-sm hover:bg-black transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60"
              >
                <Lightning size={16} weight="fill" /> {busy === "member_annual" ? "Opening checkout…" : "Go Annual"}
              </button>
            }
          />

          {/* Founding */}
          <PlanCard
            testid="plan-card-founding"
            accent
            crown
            name="Founding Council · Lifetime Lock-in"
            price={founding ? money(founding.amount) : "—"}
            period="/mo, for life"
            subnote={spotsLeft != null ? `Only ${spotsLeft} of ${data.founding_spots_total} seats left` : ""}
            blurb={founding?.blurb}
            features={FEATURES.founding}
            cta={
              <button
                data-testid="plan-cta-founding"
                disabled={!!busy || spotsLeft === 0}
                onClick={() => subscribe("founding")}
                className="w-full h-12 bg-[#B47A3C] text-white font-semibold text-sm hover:bg-[#a94a2d] transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60"
              >
                <Crown size={16} weight="fill" /> {spotsLeft === 0 ? "Seats full" : busy === "founding" ? "Opening checkout…" : "Claim my seat"}
              </button>
            }
          />
        </div>

        <p className="mt-5 text-sm text-[#57534E]" data-testid="supported-platforms-note">
          <strong className="text-[#0D1B2A]">Currently offered platforms:</strong> Chairish · 1stDibs · AptDeco · Pamono
          <span className="text-[#8A8577]"> — more on the way.</span>
        </p>

        {/* Credit packs strip */}
        <div className="mt-6 border border-[#E5E0D6] bg-white rounded-lg px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="credit-packs">
          <div className="flex items-center gap-2 shrink-0">
            <Coins size={20} weight="fill" className="text-[#B47A3C]" />
            <span className="text-sm font-bold">Top up anytime</span>
          </div>
          <p className="text-sm text-[#57534E]">
            On the Monthly plan, add cross-list credits whenever you need them —
            <strong className="text-[#0D1B2A]"> 50 credits for $20</strong> (10 more cross-lists) or
            <strong className="text-[#0D1B2A]"> 100 credits for $35</strong> (20 more). Each cross-list uses 5 credits.
            Annual & Founding are always unlimited.
          </p>
        </div>

        {/* One good flip covers your membership */}
        <div className="mt-10 grid lg:grid-cols-[1.1fr_1fr] gap-6 items-center border border-[#0D1B2A] bg-[#0D1B2A] text-[#FDFBF7] rounded-xl overflow-hidden" data-testid="flip-math">
          <div className="p-7 sm:p-9">
            <div className="flex items-center gap-2 mb-3">
              <Receipt size={20} weight="fill" className="text-[#B47A3C]" />
              <span className="label-caps text-[#B47A3C]" style={{ letterSpacing: "0.2em" }}>The math</span>
            </div>
            <h2 className="font-serif-display text-3xl sm:text-4xl leading-tight tracking-tight">
              One good flip covers your membership.
            </h2>
            <p className="text-[#D8D3C8] mt-4 leading-relaxed text-[15px]">
              Every hour you save is one you can spend sourcing the next great piece. Do the work once here, list it
              everywhere, and let a single sale more than pay for the tools that made it possible.
            </p>
            <p className="text-[11px] text-[#8A867D] italic mt-4">Illustrative example — your real numbers will vary by platform &amp; category.</p>
          </div>
          <div className="bg-[#FDFBF7] text-[#0D1B2A] m-0 lg:m-6 lg:rounded-lg p-6 sm:p-7" data-testid="flip-math-table">
            <FlipRow label="Fantastic find — you paid" value="$50" />
            <FlipRow label="Cross-listed & sold for" value="$225" />
            <FlipRow label="Est. marketplace fees (~20%)" value="− $45" muted />
            <FlipRow label="Your purchase cost" value="− $50" muted />
            <div className="h-px bg-[#E5E0D6] my-3" />
            <FlipRow label="You keep" value="≈ $130" bold />
            <div className="mt-4 rounded-md bg-[#F5F3ED] px-4 py-3 text-sm">
              That one flip covers your <strong>Monthly membership for ~3 months</strong> — or a chunk of the Annual plan.
            </div>
          </div>
        </div>

        <p className="text-sm text-[#8A8577] mt-8 max-w-2xl">
          Everything you scout and prep stays yours — always exportable. Cancel anytime; memberships are prepaid, so
          there are no surprise renewals or hidden fees.
        </p>
      </div>
    </div>
  );
}

function FlipRow({ label, value, muted, bold }) {
  return (
    <div className="flex items-center justify-between py-1.5">
      <span className={`text-sm ${muted ? "text-[#8A867D]" : "text-[#3A3730]"}`}>{label}</span>
      <span className={`tabular-nums ${bold ? "font-serif-display text-2xl font-bold" : "text-sm font-semibold"} ${muted ? "text-[#8A867D]" : "text-[#0D1B2A]"}`}>{value}</span>
    </div>
  );
}

function PlanCard({ testid, name, price, period, subnote, blurb, features, featuresLead, footNote, cta, featured, accent, crown, badge }) {
  return (
    <div
      data-testid={testid}
      className={`flex flex-col p-6 bg-white border transition-transform hover:-translate-y-1 ${
        featured ? "border-[#0D1B2A] ring-1 ring-[#0D1B2A] relative" : accent ? "border-[#B47A3C]" : "border-[#E5E0D6]"
      }`}
    >
      {featured && (
        <span className="absolute -top-3 left-6 bg-[#0D1B2A] text-[#FDFBF7] text-[11px] font-bold uppercase tracking-wide px-3 py-1">
          {badge || "Most popular"}
        </span>
      )}
      <div className="flex items-center gap-2">
        {crown && <Crown size={18} weight="fill" className="text-[#B47A3C]" />}
        <p className="label-caps">{name}</p>
      </div>
      <div className="mt-3 mb-1 flex items-end gap-1">
        <span className="font-serif-display text-4xl font-bold tracking-tight">{price}</span>
        <span className="text-sm text-[#8A8577] mb-1.5">{period}</span>
      </div>
      {subnote && <p className="text-sm font-semibold text-[#B47A3C]">{subnote}</p>}
      {blurb && <p className="text-sm text-[#57534E] mt-2 leading-relaxed">{blurb}</p>}

      <ul className="mt-5 space-y-2.5 flex-1">
        {featuresLead && <li className="text-xs font-bold uppercase tracking-wide text-[#8A8577]">{featuresLead}</li>}
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-sm text-[#3A3730]">
            <Check size={16} weight="bold" className="text-[#B47A3C] mt-0.5 shrink-0" />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      {footNote && <p className="text-[11px] text-[#8A867D] mt-4 leading-snug border-t border-[#F0EDE6] pt-3">{footNote}</p>}
      <div className="mt-6">{cta}</div>
    </div>
  );
}
